// src/components/common/GlobalStyles.js
import { Global, css } from '@emotion/react';

// Rengigs Official Logo Palette
const rengigsPalette = {
  forestGreen: '#2E7D32',
  deepForest: '#1B5E20',
  mediumGreen: '#4CAF50',
  lightGreen: '#8BC34A',
  brandLime: '#CDDC39',
  charcoal: '#1A2A3A',
  softGreen: 'rgba(46, 125, 50, 0.08)',
  white: '#FFFFFF',
  offWhite: '#F8F9FA',
  border: '#E1E9F0',
  gradient: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%)',
  gradientLight: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 50%, #CDDC39 100%)',
  gradientSubtle: 'linear-gradient(135deg, #E8F5E9 0%, #F1F8E9 100%)',
};

const GlobalStyles = () => (
  <Global
    styles={css`
      /* =========================
         ANIMATIONS
      ========================= */
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }

      @keyframes float {
        0% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
        100% { transform: translateY(0px); }
      }

      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }

      @keyframes shimmer {
        0% { background-position: -1000px 0; }
        100% { background-position: 1000px 0; }
      }

      @keyframes slideInUp {
        from {
          transform: translateY(30px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      /* =========================
         BRAND COLORS - RENGIGS GRADIENT GREEN
      ========================= */
      :root {
        --primary: #2E7D32;
        --primary-light: #4CAF50;
        --primary-lighter: #8BC34A;
        --primary-lime: #CDDC39;
        --primary-dark: #1B5E20;
        --primary-soft: #E8F5E9;
        --primary-muted: #C8E6C9;
        --accent: #CDDC39;
        --accent-light: #DCE775;
        --text-dark: #1A2A3A;
        --text-muted: #6B7280;
        --text-light: #9CA3AF;
        --border: #E5E7EB;
        --border-light: #F3F4F6;
        --background: #F8F9FA;
        --gradient: linear-gradient(135deg, #2E7D32 0%, #4CAF50 40%, #8BC34A 70%, #CDDC39 100%);
        --gradient-reverse: linear-gradient(135deg, #CDDC39 0%, #8BC34A 40%, #4CAF50 70%, #2E7D32 100%);
        --gradient-vertical: linear-gradient(180deg, #2E7D32 0%, #4CAF50 100%);
        --gradient-horizontal: linear-gradient(90deg, #2E7D32 0%, #4CAF50 50%, #8BC34A 100%);
      }

      /* =========================
         CUSTOM SCROLLBAR - RENGIGS GRADIENT GREEN
      ========================= */
      *::-webkit-scrollbar {
        width: 10px;
        height: 10px;
      }

      *::-webkit-scrollbar-track {
        background: var(--background);
        border-radius: 10px;
      }

      *::-webkit-scrollbar-thumb {
        background: var(--gradient);
        border-radius: 10px;
        transition: background 0.3s ease;
      }

      *::-webkit-scrollbar-thumb:hover {
        background: var(--gradient-reverse);
      }

      * {
        scrollbar-width: thin;
        scrollbar-color: var(--primary) var(--background);
      }

      /* =========================
         GRADIENT TEXT
      ========================= */
      .gradient-text {
        background: var(--gradient);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        background-size: 300% 300%;
        animation: gradientShift 6s ease infinite;
      }

      .gradient-text-primary {
        background: linear-gradient(135deg, var(--primary), var(--primary-light));
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      .gradient-text-lime {
        background: linear-gradient(135deg, var(--primary-lighter), var(--primary-lime));
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }

      /* =========================
         EFFECTS
      ========================= */
      .float-animation {
        animation: float 3s ease-in-out infinite;
      }

      .pulse-animation {
        animation: pulse 2s ease-in-out infinite;
      }

      .shimmer {
        background: linear-gradient(
          90deg,
          transparent,
          rgba(76, 175, 80, 0.2),
          transparent
        );
        background-size: 1000px 100%;
        animation: shimmer 2s infinite;
      }

      .hover-scale {
        transition: transform 0.3s;
        &:hover {
          transform: scale(1.05);
        }
      }

      .hover-lift {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        &:hover {
          transform: translateY(-6px);
          box-shadow: 0 15px 30px rgba(46, 125, 50, 0.2);
        }
      }

      .slide-in-up {
        animation: slideInUp 0.6s ease-out;
      }

      /* =========================
         BORDER + GLASS
      ========================= */
      .rainbow-border {
        border: 2px solid transparent;
        border-radius: 12px;
        background:
          linear-gradient(white, white) padding-box,
          var(--gradient) border-box;
      }

      .glass-effect {
        background: rgba(255, 255, 255, 0.6);
        backdrop-filter: blur(12px);
        border: 1px solid rgba(46, 125, 50, 0.2);
      }

      .glass-effect-dark {
        background: rgba(26, 42, 58, 0.6);
        backdrop-filter: blur(12px);
        border: 1px solid rgba(76, 175, 80, 0.3);
      }

      /* =========================
         GLOW EFFECTS
      ========================= */
      .neon-glow {
        box-shadow:
          0 0 5px rgba(46, 125, 50, 0.4),
          0 0 10px rgba(76, 175, 80, 0.3);
      }

      .green-glow {
        box-shadow: 0 0 20px rgba(46, 125, 50, 0.3);
      }

      .green-glow-hover {
        transition: box-shadow 0.3s;
        &:hover {
          box-shadow: 0 0 25px rgba(46, 125, 50, 0.4);
        }
      }

      /* =========================
         COLOR UTILITIES
      ========================= */
      .color-primary { color: var(--primary); }
      .color-primary-light { color: var(--primary-light); }
      .color-primary-lighter { color: var(--primary-lighter); }
      .color-accent { color: var(--accent); }
      .color-muted { color: var(--text-muted); }
      .color-dark { color: var(--text-dark); }

      /* =========================
         BACKGROUND GRADIENTS
      ========================= */
      .bg-gradient-primary {
        background: var(--gradient);
      }

      .bg-gradient-primary-reverse {
        background: var(--gradient-reverse);
      }

      .bg-gradient-vertical {
        background: var(--gradient-vertical);
      }

      .bg-gradient-horizontal {
        background: var(--gradient-horizontal);
      }

      .bg-gradient-soft {
        background: var(--gradient-subtle);
      }

      .bg-primary-light {
        background: var(--primary-light);
      }

      .bg-primary-soft {
        background: var(--primary-soft);
      }

      /* =========================
         TYPOGRAPHY
      ========================= */
      h1, h2, h3, h4, h5, h6 {
        letter-spacing: -0.02em;
        color: var(--text-dark);
        font-weight: 700;
      }

      .display-1 {
        font-size: clamp(3rem, 8vw, 5rem);
        font-weight: 800;
        line-height: 1.1;
      }

      .display-2 {
        font-size: clamp(2.5rem, 6vw, 4rem);
        font-weight: 700;
        line-height: 1.2;
      }

      .heading-xl {
        font-size: clamp(2rem, 4vw, 3rem);
        font-weight: 800;
        letter-spacing: -0.02em;
      }

      .heading-lg {
        font-size: clamp(1.5rem, 3vw, 2rem);
        font-weight: 700;
      }

      /* =========================
         CARD EFFECTS
      ========================= */
      .card-hover-effect {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        border: 1px solid var(--border);
        &:hover {
          transform: translateY(-6px);
          border-color: var(--primary-light);
          box-shadow: 0 20px 40px rgba(46, 125, 50, 0.15);
        }
      }

      .card-gradient-hover {
        transition: all 0.3s;
        &:hover {
          background: var(--gradient);
          color: white;
          .card-icon {
            color: white;
          }
        }
      }

      /* =========================
         BUTTON STYLES
      ========================= */
      .btn-gradient {
        background: var(--gradient);
        background-size: 200% auto;
        color: white;
        border: none;
        transition: all 0.3s;
        &:hover {
          transform: translateY(-2px);
          background-position: right center;
          box-shadow: 0 10px 25px rgba(46, 125, 50, 0.3);
        }
      }

      .btn-outline-green {
        border: 2px solid var(--primary);
        color: var(--primary);
        transition: all 0.3s;
        &:hover {
          background: var(--gradient);
          border-color: transparent;
          color: white;
          transform: translateY(-2px);
        }
      }

      .btn-solid-green {
        background: var(--primary);
        color: white;
        transition: all 0.3s;
        &:hover {
          background: var(--primary-dark);
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(46, 125, 50, 0.3);
        }
      }

      /* =========================
         SCROLL ANIMATION
      ========================= */
      .fade-in {
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .fade-in.visible {
        opacity: 1;
        transform: translateY(0);
      }

      /* =========================
         UTILITY CLASSES
      ========================= */
      .cursor-pointer {
        cursor: pointer;
      }

      .transition-smooth {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .rounded-xl {
        border-radius: 16px;
      }

      .rounded-2xl {
        border-radius: 20px;
      }

      .shadow-green {
        box-shadow: 0 4px 14px 0 rgba(46, 125, 50, 0.2);
      }

      .shadow-green-hover {
        transition: box-shadow 0.3s;
        &:hover {
          box-shadow: 0 8px 25px 0 rgba(46, 125, 50, 0.3);
        }
      }
    `}
  />
);

export default GlobalStyles;